import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { getApiUrl, isBackendConfigured } from '../config';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sync, setSync] = useState(null);

  const loadProducts = useCallback(async (refresh = false) => {
    if (!isBackendConfigured()) {
      setError('Store menu unavailable. Backend URL is not configured.');
      setLoading(false);
      return;
    }

    setError('');
    try {
      const response = await axios.get(getApiUrl('/api/products'), {
        params: refresh ? { refresh: true } : undefined,
      });
      setProducts(response.data.products || []);
      setCategories(response.data.categories || []);
      setSync(response.data.sync || null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const syncInfo = err.response?.data?.sync;
      setError(detail || 'Could not load products from the store.');
      if (syncInfo) setSync(syncInfo);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    const interval = setInterval(() => loadProducts(false), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadProducts]);

  return (
    <ProductContext.Provider
      value={{
        products,
        categories,
        loading,
        error,
        sync,
        refreshProducts: () => loadProducts(true),
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
